"use client";

import { cn } from "@/lib/utils";

// ── Strength calculation ────────────────────────────────────────

interface StrengthResult {
  score: number; // 0–4
  label: string;
  color: string;
  barColor: string;
  checks: {
    label: string;
    met: boolean;
  }[];
}

function calculateStrength(password: string): StrengthResult {
  const checks = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains a number", met: /\d/.test(password) },
    { label: "Contains uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Contains special character", met: /[^a-zA-Z0-9]/.test(password) },
  ];

  const score = checks.filter((c) => c.met).length;

  const levels: { label: string; color: string; barColor: string }[] = [
    { label: "", color: "text-muted-foreground", barColor: "bg-muted" },
    { label: "Weak", color: "text-red-500", barColor: "bg-red-400" },
    { label: "Fair", color: "text-yellow-500", barColor: "bg-yellow-400" },
    { label: "Good", color: "text-blue-500", barColor: "bg-blue-400" },
    { label: "Strong", color: "text-green-600", barColor: "bg-green-500" },
  ];

  return {
    score,
    ...levels[score],
    checks,
  };
}

// ── Component ───────────────────────────────────────────────────

interface PasswordStrengthIndicatorProps {
  password: string;
  /** Show the requirements checklist (for reset password page) */
  showChecklist?: boolean;
}

export function PasswordStrengthIndicator({
  password,
  showChecklist = false,
}: PasswordStrengthIndicatorProps) {
  if (!password) return null;

  const { score, label, color, barColor, checks } = calculateStrength(password);

  return (
    <div className="space-y-2 mt-1.5">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex gap-1 flex-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-all duration-300",
                i <= score ? barColor : "bg-muted"
              )}
            />
          ))}
        </div>
        {label && (
          <span className={cn("text-xs font-medium tabular-nums", color)}>
            {label}
          </span>
        )}
      </div>

      {/* Requirements checklist */}
      {showChecklist && (
        <ul className="space-y-1">
          {checks.map((check) => (
            <li key={check.label} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  "size-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200",
                  check.met
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {check.met ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 10 10"
                    className="size-2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1.5 5l2.5 2.5 4.5-4" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 10 10"
                    className="size-2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M2.5 2.5l5 5M7.5 2.5l-5 5" />
                  </svg>
                )}
              </span>
              <span
                className={cn(
                  "transition-colors duration-200",
                  check.met ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {check.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
